import type { ReactNode } from "react"
import { useState } from "react"
import { TinyColor } from "@ctrl/tinycolor"
import {
	IconArrowUpDownTrailFill18 as ArrowDownUp,
	IconOffice3Fill18 as Building2,
	IconCircleDashedFill18 as CircleDot,
	IconMapFill18 as MapIcon,
	IconMessageBubbleUserFill18 as SourceIcon,
	IconTag2Fill18 as TagIcon,
	IconUsersFill18 as Users,
} from "nucleo-ui-fill-18"
import {
	IconCheckOutline18 as Check,
	IconMessageBubbleUserOutline18 as CustomerViewIcon,
	IconBarsFilterOutline18 as ListFilterIcon,
	IconPlusOutline18 as Plus,
	IconLayersOutline18 as Stack,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	Badge,
	Button,
	IconButton,
	IssueStatusIcon,
	PortalSectionHeader,
	Switch,
} from "@productlane/ui"
import {
	Command,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { NestedCommand } from "@productlane/ui/src/components/internal/nested-command"

import type { FilterChip } from "@/components/filters"
import { FilterControlChips } from "@/components/filters"
import { isDevEnvironment } from "@/env"
import { issuePriorityIcon } from "@/screens/app/issues/issue-priority-icon"
import { PageHeader } from "@/screens/app/page-header"
import { PortalTree } from "@/screens/app/roadmap/portal-tree"
import { NotFound } from "@/screens/not-found"
import { DesignIssueHeader } from "./design-issue-header"

// Fictional example data only: this page ships in the prod bundle.
interface MockLabel {
	name: string
	color: string
}

const LABELS: Record<string, MockLabel> = {
	internal: { name: "Internal idea", color: "#95a2b3" },
	bug: { name: "Bug", color: "#eb5757" },
	feature: { name: "Feature", color: "#bb87fc" },
	improvement: { name: "Improvement", color: "#4ea7fc" },
	design: { name: "Design", color: "#f2994a" },
}

interface MockIssue {
	identifier: string
	title: string
	status: string
	statusColor: string
	priority: string
	labels: (keyof typeof LABELS)[]
	requests: number
	score: number
}

const IN_PROGRESS: MockIssue[] = [
	{
		identifier: "ENG-412",
		title: "CSV export for invoices",
		status: "started",
		statusColor: "#f2c94c",
		priority: "High",
		labels: ["feature"],
		requests: 14,
		score: 42,
	},
	{
		identifier: "ENG-398",
		title: "SSO with Okta",
		status: "started",
		statusColor: "#f2c94c",
		priority: "Urgent",
		labels: ["feature"],
		requests: 9,
		score: 31,
	},
	{
		identifier: "ENG-455",
		title: "Move billing to the new pricing table",
		status: "started",
		statusColor: "#f2c94c",
		priority: "Medium",
		labels: ["internal"],
		requests: 0,
		score: 0,
	},
]

const TODO: MockIssue[] = [
	{
		identifier: "ENG-371",
		title: "Slack thread replies sync both ways",
		status: "unstarted",
		statusColor: "#bec2c8",
		priority: "High",
		labels: ["improvement"],
		requests: 11,
		score: 27,
	},
	{
		identifier: "ENG-466",
		title: "Rework the onboarding checklist",
		status: "unstarted",
		statusColor: "#bec2c8",
		priority: "Low",
		labels: ["internal", "design"],
		requests: 0,
		score: 0,
	},
	{
		identifier: "ENG-342",
		title: "Attachments disappear after editing a reply",
		status: "unstarted",
		statusColor: "#bec2c8",
		priority: "Urgent",
		labels: ["bug"],
		requests: 6,
		score: 18,
	},
	{
		identifier: "ENG-470",
		title: "Dark mode for the public changelog",
		status: "unstarted",
		statusColor: "#bec2c8",
		priority: "Medium",
		labels: ["feature"],
		requests: 4,
		score: 9,
	},
	{
		identifier: "ENG-481",
		title: "Clean up old webhook retry logic",
		status: "unstarted",
		statusColor: "#bec2c8",
		priority: "No priority",
		labels: ["internal"],
		requests: 0,
		score: 0,
	},
]

const GROUPS = [
	{
		label: "In Progress",
		status: "started",
		color: "#f2c94c",
		issues: IN_PROGRESS,
	},
	{ label: "Todo", status: "unstarted", color: "#bec2c8", issues: TODO },
]

const isInternalIdea = (issue: MockIssue) => issue.labels.includes("internal")
const isFromCustomers = (issue: MockIssue) => issue.requests > 0

type IssueFilter = (issue: MockIssue) => boolean

const SINGLE_PORTAL = [
	{ id: null, name: "Main portal", logoUrl: null, colorSeed: null },
]

const NAV_ROW =
	"flex h-[1.9rem] w-full min-w-0 items-center gap-2 rounded-md pl-2 pr-2 text-sm font-medium"
const NAV_SELECTED = "bg-gray-100 text-primary dark:bg-gray-800"
const NAV_IDLE = "bg-transparent hover:bg-gray-100 hover:dark:bg-gray-800/50"

function NewBadge({ children = "New" }: { children?: string }) {
	return (
		<Badge size="sm" variant="green" className="flex-none">
			{children}
		</Badge>
	)
}

function LabelDot({ color }: { color: string }) {
	return (
		<span
			className="h-[9px] w-[9px] flex-none rounded-full"
			style={{
				backgroundColor: new TinyColor(color).setAlpha(0.8).toString(),
			}}
		/>
	)
}

function LabelPill({ label }: { label: MockLabel }) {
	return (
		<Badge size="sm" variant="default" className="gap-1.5 font-normal">
			<LabelDot color={label.color} />
			{label.name}
		</Badge>
	)
}

function SavedViews({
	views,
	activeView,
	isEditing,
}: {
	views: { name: string; Icon: typeof Stack }[]
	activeView?: string
	isEditing?: boolean
}) {
	return (
		<div className="px-2.5 pb-3">
			<ul className="flex list-none flex-col gap-px">
				<li className="flex items-center gap-2 px-2 pb-1 text-xs font-medium text-tertiary">
					Views
					<NewBadge />
				</li>
				{views.map(({ name, Icon }) => (
					<li key={name}>
						{isEditing && name === activeView ? (
							<div className="flex h-[1.9rem] items-center gap-2 rounded-md border border-gray-200 bg-gray-50 pl-2 pr-2 text-sm font-medium dark:border-gray-700/80 dark:bg-gray-850">
								<Icon
									className="size-3.5 flex-none text-secondary"
									strokeWidth={1}
								/>
								<span className="flex-1 truncate">{name}</span>
								<span className="h-4 w-px flex-none animate-pulse bg-brand-600" />
							</div>
						) : (
							<button
								type="button"
								className={cn(
									NAV_ROW,
									name === activeView ? NAV_SELECTED : NAV_IDLE,
								)}
							>
								<Icon
									className="size-3.5 flex-none text-secondary"
									strokeWidth={1}
								/>
								<span className="flex-1 truncate text-start">{name}</span>
							</button>
						)}
					</li>
				))}
				<li>
					<button
						type="button"
						className={cn(NAV_ROW, NAV_IDLE, "text-secondary")}
					>
						<Plus
							className="size-3.5 flex-none text-tertiary"
							strokeWidth={1}
						/>
						<span className="flex-1 truncate text-start">New view</span>
					</button>
				</li>
			</ul>
		</div>
	)
}

function IssueRow({
	issue,
	maxScore,
	showSource,
}: {
	issue: MockIssue
	maxScore: number
	showSource?: boolean
}) {
	const percent = maxScore ? (issue.score / maxScore) * 100 : 0
	return (
		<div className="group relative grid min-h-[2.8rem] grid-cols-[auto_auto_auto_1fr_auto_auto_auto] items-center gap-3 py-1 pl-2 pr-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-850">
			<div className="flex items-center" />
			<div className="flex items-center gap-3">
				<IconButton size="md" variant="ghost-brightness">
					<IssueStatusIcon
						className="h-3.5 min-h-3.5 w-3.5 min-w-3.5 overflow-visible"
						status={issue.status}
						color={issue.statusColor}
					/>
				</IconButton>
				<div className="hidden flex-none tabular-nums text-gray-500 md:block">
					{issue.identifier}
				</div>
				<IconButton
					size="md"
					variant="ghost-brightness"
					aria-label={`Priority: ${issue.priority}`}
					Icon={issuePriorityIcon(issue.priority)}
					iconClassName="h-4 w-4"
					className="hidden md:flex"
				/>
			</div>
			<div className="flex min-w-0 items-center gap-3 font-medium">
				<span className="min-w-0 truncate">{issue.title}</span>
			</div>
			<div className="flex items-center justify-end gap-1.5">
				{showSource && (
					<Badge size="sm" variant="light-gray" className="font-normal">
						{isFromCustomers(issue) ? `${issue.requests} requests` : "Internal"}
					</Badge>
				)}
				{issue.labels.map((key) => (
					<LabelPill key={key} label={LABELS[key]!} />
				))}
			</div>
			<div className="flex w-20 flex-none items-center gap-2 text-gray-700 dark:text-gray-300">
				<div className="relative h-1 w-10 rounded bg-gray-200 dark:bg-gray-800">
					<div
						className="absolute h-1 rounded bg-green-500"
						style={{ width: `${percent}%` }}
					/>
				</div>
				<p className="w-7">{issue.score}</p>
			</div>
			<div className="flex min-w-7 flex-none justify-end">
				<Switch name={`visibility-${issue.identifier}`} checked size="small" />
			</div>
		</div>
	)
}

function RoadmapList({
	filter,
	showSource,
}: {
	filter?: IssueFilter
	showSource?: boolean
}) {
	return (
		<div className="flex-1 overflow-y-auto pb-2 pt-2">
			{GROUPS.map((group) => {
				const issues = filter ? group.issues.filter(filter) : group.issues
				if (issues.length === 0) return null
				const maxScore = Math.max(...issues.map((i) => i.score))
				return (
					<div key={group.label}>
						<PortalSectionHeader
							className="mx-3 w-[calc(100%-1.5rem)] rounded-lg"
							statusType="issue"
							status={group.status}
							statusColor={group.color}
							count={issues.length}
						>
							{group.label}
						</PortalSectionHeader>
						{issues.map((issue) => (
							<IssueRow
								key={issue.identifier}
								issue={issue}
								maxScore={maxScore}
								showSource={showSource}
							/>
						))}
					</div>
				)
			})}
		</div>
	)
}

function noop() {
	return undefined
}

const labelChip = (): FilterChip => ({
	key: "Labels",
	label: "Label",
	valueCount: 1,
	isNegated: true,
	onNegatedChange: noop,
	valueText: "Internal idea",
	onClear: noop,
	popoverContent: <LabelItems selected={["internal"]} />,
})

const sourceChip = (): FilterChip => ({
	key: "Source",
	label: "Source",
	middleText: "is",
	valueText: "From customers",
	onClear: noop,
	popoverContent: <SourceItems selected="customers" />,
})

function Chips({
	chips,
	showSave,
}: {
	chips: FilterChip[]
	showSave?: boolean
}) {
	return (
		<FilterControlChips
			chips={chips}
			tailContent={
				showSave ? (
					<div className="ml-auto flex items-center gap-2">
						<NewBadge />
						<Button type="button">Save as view</Button>
					</div>
				) : undefined
			}
		/>
	)
}

function LabelItems({ selected }: { selected: string[] }) {
	return (
		<Command>
			<CommandInput placeholder="Filter..." />
			<CommandList className="max-h-[min(70vh,320px)]">
				<CommandGroup>
					{Object.entries(LABELS).map(([key, label]) => (
						<CommandItem key={key}>
							<div className="flex w-full items-center gap-2">
								<span className="ml-1 flex">
									<LabelDot color={label.color} />
								</span>
								<span className="flex-1 truncate">{label.name}</span>
								<Check
									className={cn("h-4 w-4 shrink-0", {
										"opacity-0": !selected.includes(key),
									})}
								/>
							</div>
						</CommandItem>
					))}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

function SourceItems({ selected }: { selected?: "customers" | "internal" }) {
	const options = [
		{
			key: "customers" as const,
			label: "From customers",
			hint: "At least one customer request",
		},
		{
			key: "internal" as const,
			label: "Internal",
			hint: "No customer requests",
		},
	]
	return (
		<Command>
			<CommandList>
				<CommandGroup>
					{options.map((option) => (
						<CommandItem key={option.key}>
							<div className="flex w-full items-center gap-2">
								<div className="flex min-w-0 flex-1 flex-col">
									<span className="truncate">{option.label}</span>
									<span className="truncate text-xs text-tertiary">
										{option.hint}
									</span>
								</div>
								<Check
									className={cn("h-4 w-4 shrink-0", {
										"opacity-0": selected !== option.key,
									})}
								/>
							</div>
						</CommandItem>
					))}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

type FilterType =
	| "Statuses"
	| "Labels"
	| "Source"
	| "Teams"
	| "Projects"
	| "Priorities"
	| "Companies"

const FILTER_CATEGORIES = [
	{ type: "Statuses" as const, label: "Status", Icon: CircleDot },
	{
		type: "Labels" as const,
		label: "Label",
		Icon: TagIcon,
		badge: <NewBadge />,
	},
	{
		type: "Source" as const,
		label: "Source",
		Icon: SourceIcon,
		badge: <NewBadge />,
	},
	{ type: "Teams" as const, label: "Team", Icon: Users },
	{ type: "Projects" as const, label: "Project", Icon: MapIcon },
	{ type: "Priorities" as const, label: "Priority", Icon: ArrowDownUp },
	{ type: "Companies" as const, label: "Company", Icon: Building2 },
]

// The real FilterPopover's content, held open on one group so the menu is
// always visible on this page.
function OpenFilterMenu({ group }: { group: "Labels" | "Source" }) {
	const [active, setActive] = useState<FilterType>(group)
	return (
		<div className="absolute right-3 top-11 z-50 w-auto rounded-lg border border-gray-200 bg-gray-25 text-primary shadow-lg dark:border-gray-700/70 dark:bg-gray-850">
			<NestedCommand<FilterType>
				groups={FILTER_CATEGORIES}
				activeGroup={active}
				onGroupChange={(next) => setActive(next ?? group)}
				header={<CommandInput placeholder="Filter..." />}
				mainPanelClassName="w-52"
				subPanelClassName="w-64"
				subPanelSide="left"
				renderSubContent={(g) =>
					g === "Source" ? (
						<SourceItems selected="customers" />
					) : (
						<LabelItems selected={g === "Labels" ? ["internal"] : []} />
					)
				}
			/>
		</div>
	)
}

function RoadmapFrame({
	title,
	views,
	activeView,
	isEditingView,
	chips,
	showSave,
	filter,
	showSource,
	menu,
	height = "h-[600px]",
}: {
	title: string
	views: { name: string; Icon: typeof Stack }[]
	activeView?: string
	isEditingView?: boolean
	chips: FilterChip[]
	showSave?: boolean
	filter?: IssueFilter
	showSource?: boolean
	menu?: ReactNode
	height?: string
}) {
	const viewOpen = activeView !== undefined
	return (
		<div
			className={cn(
				"flex w-full max-w-[1280px] overflow-hidden rounded-[10px] bg-white shadow-sm ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10",
				height,
			)}
		>
			<div className="flex h-full w-64 flex-shrink-0 flex-col overflow-y-auto border-r border-gray-200 bg-white dark:border-gray-700/80 dark:bg-gray-900">
				<PageHeader
					title="Feature requests"
					className="border-b lg:pl-2.5 lg:pr-3"
					titleClassName="lg:ml-[7.5px]"
				/>
				<PortalTree
					portals={SINGLE_PORTAL}
					activePortalId={viewOpen ? null : undefined}
					onPortalChange={noop}
					tabValue="all"
					onTabChange={noop}
					pendingCount={7}
				/>
				<SavedViews
					views={views}
					activeView={activeView}
					isEditing={isEditingView}
				/>
			</div>
			<div className="relative flex flex-shrink flex-grow flex-col overflow-hidden">
				<PageHeader
					title={<span className="text-sm font-medium">{title}</span>}
					titleClassName="text-sm font-medium"
					className="pl-[17.5px] pr-3"
					actions={
						<div className="flex items-center gap-2">
							<IconButton
								variant="ghost"
								Icon={ListFilterIcon}
								iconStrokeWidth={1.75}
								className={menu ? "bg-gray-100 dark:bg-gray-800" : undefined}
							/>
							<Button variant="outline">Publish</Button>
						</div>
					}
				/>
				{menu}
				<Chips chips={chips} showSave={showSave} />
				<RoadmapList filter={filter} showSource={showSource} />
			</div>
		</div>
	)
}

function Section({
	crumb,
	note,
	children,
}: {
	crumb: string
	note?: string
	children: ReactNode
}) {
	return (
		<div className="space-y-3">
			<div className="text-xs font-medium text-tertiary">{crumb}</div>
			{note && (
				<div className="max-w-[704px] text-xxs text-tertiary">{note}</div>
			)}
			{children}
		</div>
	)
}

const ALL_VIEW = { name: "All requests", Icon: Stack }
const CUSTOMER_VIEW = { name: "Customer requests", Icon: CustomerViewIcon }

export function RoadmapLabelViewsPage() {
	if (!isDevEnvironment) return <NotFound />

	const notInternal: IssueFilter = (issue) => !isInternalIdea(issue)

	return (
		<div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
			<div className="mx-auto max-w-[1440px] px-8 py-10 pb-24">
				<div className="mb-10 max-w-[704px] space-y-4">
					<DesignIssueHeader
						identifier="Design"
						title="Feature requests: label filter and saved views"
						url="/edit-requests"
						summary="Filter the roadmap by Linear label, with is and is not. Save any filter as a view in the sidebar and switch between views in one click. A Source filter splits customer requests from internal work by linked requests alone, so it works before labels sync."
					/>
					<div className="space-y-2 rounded-lg bg-white p-5 text-sm text-secondary ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
						<div className="font-medium text-primary">Open decisions</div>
						<ol className="list-decimal space-y-1.5 pl-5">
							<li>Ship Label, Source, or both.</li>
							<li>
								Views shared with the whole workspace, or private to each user.
								The threads views are shared today.
							</li>
							<li>
								Label pills on each row: always shown, or only while a label
								filter is on.
							</li>
						</ol>
					</div>
				</div>

				<div className="space-y-14">
					<Section
						crumb="1. Label filter, before saving"
						note="Label is not Internal idea hides ENG-455, ENG-466 and ENG-481. Save as view turns the filter into a sidebar view."
					>
						<RoadmapFrame
							title="All requests"
							views={[ALL_VIEW]}
							chips={[labelChip()]}
							showSave
							filter={notInternal}
						/>
					</Section>

					<Section
						crumb="2. Filter menu, Label open"
						note="Label sits next to Status. Linear's label colours show as dots, like Linear's own label menu."
					>
						<RoadmapFrame
							title="All requests"
							views={[ALL_VIEW]}
							chips={[labelChip()]}
							showSave
							filter={notInternal}
							menu={<OpenFilterMenu group="Labels" />}
						/>
					</Section>

					<Section
						crumb="3. Saved view, renaming right after save"
						note="Save adds the view and opens its name for editing, like on threads."
					>
						<RoadmapFrame
							title="Customer requests"
							views={[ALL_VIEW, CUSTOMER_VIEW]}
							activeView="Customer requests"
							isEditingView
							chips={[labelChip()]}
							filter={notInternal}
						/>
					</Section>

					<Section
						crumb="4. Saved view open"
						note="The header shows the view name. The filter stays editable; a change offers Save again."
					>
						<RoadmapFrame
							title="Customer requests"
							views={[ALL_VIEW, CUSTOMER_VIEW]}
							activeView="Customer requests"
							chips={[labelChip()]}
							filter={notInternal}
						/>
					</Section>

					<Section
						crumb="5. Alternative: Source filter"
						note="From customers keeps issues with at least one linked request. It needs no label sync. Rows show the request count."
					>
						<RoadmapFrame
							title="All requests"
							views={[ALL_VIEW]}
							chips={[sourceChip()]}
							showSave
							filter={isFromCustomers}
							showSource
							menu={<OpenFilterMenu group="Source" />}
						/>
					</Section>
				</div>
			</div>
		</div>
	)
}
